import { useLingui } from "@lingui/react/macro";
import { EyeIcon, EyeOffIcon } from "lucide-react";
import { observer } from "mobx-react-lite";
import React, { useRef, useState } from "react";

import { cn } from "../../../utils/shadcn-ui";
import { useFormFieldId } from "../form/form-field-id-context";
import { BaseInput } from "./base-input";
import styles from "./password-input.module.css";
import { FieldProps, useField, useModelBinding } from "./utils";
import { ValidationTooltip } from "./validation-tooltip";

interface Props<M extends object> extends FieldProps<M, string> {
  id?: string;
  width?: number;
  required?: boolean;
  disabled?: boolean;
  autofocus?: boolean;
}

export const PasswordInput = observer(function PasswordInput<M extends object>({
  id,
  width,
  required,
  disabled,
  autofocus,
  ...restProps
}: Props<M>) {
  const formFieldId = useFormFieldId();
  const elementRef = useRef<HTMLInputElement>(null);
  const [showPassword, setShowPassword] = useState(false);
  const { t } = useLingui();
  const { isInvalid, tooltip } = useField(elementRef);

  const { value: bindingValue, onChange } = useModelBinding(restProps);
  const value = bindingValue ?? "";

  return (
    <ValidationTooltip model={tooltip} className={styles.container}>
      <BaseInput
        type={showPassword ? "text" : "password"}
        id={id ?? formFieldId}
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        style={{ width: width ? `${width}px` : undefined, maxWidth: "100%" }}
        required={required}
        ref={elementRef}
        className={cn("min-w-[180px] pr-8", isInvalid && "border-destructive")}
        disabled={disabled}
        autoFocus={autofocus}
      />
      <button
        type="button"
        className={styles.toggleButton}
        onClick={() => setShowPassword((prev) => !prev)}
        tabIndex={-1}
        aria-label={showPassword ? t`Hide password` : t`Show password`}
      >
        {showPassword ? <EyeOffIcon size={16} /> : <EyeIcon size={16} />}
      </button>
    </ValidationTooltip>
  );
});
